import React from 'react';
import {Navigation} from '../../Navigation/Navigation';
import {ProductDescription} from '../../ProductDescription/ProductDescription';
import {InfoSectionLayout} from '../../InfoSectionLayout/InfoSectionLayout';
import {ProductOptions} from '../../ProductOptions/ProductOptions';
import {ProductSocial} from '../../ProductSocial/ProductSocial';
import {ProductName} from '../../ProductName/ProductName';
import {ProductSku} from '../../ProductSku/ProductSku';

import s from './SleekLayout.scss';
import {Cell} from '../Cell/Cell';
import {ProductGalleryLayout} from '../../ProductGallery/ProductGalleryLayout/ProductGalleryLayout';
import {LayoutComponentProps} from '../../../types/app-types';
import {Layout, MarginBottom, ProductPageSlotIds} from '../../../constants';
import {createLayoutConfigWithDefaults} from '../../ProductGallery/ProductGalleryLayout/ProductGalleryLayoutUtils';
import {BottomPlaceholder} from '../../Placeholders/BottomPlaceholder';
import {DetailsPlaceholder} from '../../Placeholders/DetailsPlaceholder';
import {ProductPageButtonsContainer} from '../../ProductPageButtonsContainer/ProductPageButtonsContainer';
import {ProductPrice} from '../../ProductPrice/ProductPrice';
import {ProductPaymentBreakdown} from '../../ProductPaymentBreakdown/ProductPaymentBreakdown';
import {ProductDiscountName} from '../../ProductDiscountName/ProductDiscountName';
import {RatingSummarySlot} from '../../RatingSummarySlot/RatingSummarySlot';
import {ReviewsSlot} from '../../ReviewsSlot/ReviewsSlot';
import {ProductPageTopSlot} from '../../ProductPageTopSlot/ProductPageTopSlot';
import classNames from 'classnames';
import {SlotsPlaceholder} from '@wix/widget-plugins-ooi';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';

export const SleekLayout: React.FunctionComponent<LayoutComponentProps> = ({settings, globals}) => {
  const GALLERY_WIDTH = 980;
  const GALLERY_HEIGHT = 551;

  const {product, slots, hasDiscountName, hasPaymentOptionsBreakdown} = globals;
  const {renderProductPageSlots} = globals.experiments ?? {};
  const isPluginInstalledInTopSlot = !!slots?.[ProductPageSlotIds.ProductPageTop];
  const isPluginInstalledInDetails1Slot = !!slots?.[ProductPageSlotIds.ProductPageDetails1];
  const isPluginInstalledInDetails9Slot = !!slots?.[ProductPageSlotIds.ProductPageDetails9];
  const isPluginInstalledInDetails2Slot = !!slots?.[ProductPageSlotIds.ProductPageDetails2];
  const isPluginInstalledInDetails3Slot = !!slots?.[ProductPageSlotIds.ProductPageDetails3];
  const isPluginInstalledInDetails4Slot = !!slots?.[ProductPageSlotIds.ProductPageDetails4];
  const isPluginInstalledInDetails8Slot = !!slots?.[ProductPageSlotIds.ProductPageDetails8];
  const isPluginInstalledInBottomSlot = !!slots?.[ProductPageSlotIds.ProductPageBottom];

  return (
    <article className={s.container}>
      <div className={classNames({[s.topSlot]: isPluginInstalledInTopSlot})}>
        <ProductPageTopSlot />
      </div>
      <Cell className={s.main}>
        <ConditionalRender by="productPage_navigation">
          <Navigation
            className={classNames({
              [s.navigation]: !isPluginInstalledInTopSlot,
              [s.navigationSmallSpacing]: isPluginInstalledInTopSlot,
            })}
          />
        </ConditionalRender>
      </Cell>
      <div className={s.headerContainer}>
        <header className={s.header}>
          <ProductGalleryLayout
            product={product}
            layoutConfig={createLayoutConfigWithDefaults({
              marginBottom: MarginBottom.LARGE,
              dimensions: {
                mainMedia: {
                  widthConf: {num: GALLERY_WIDTH, unit: 'px'},
                  heightConf: {num: GALLERY_HEIGHT, unit: 'px'},
                },
                thumbnails: {
                  widthConf: {num: GALLERY_WIDTH, unit: 'px'},
                  heightConf: {num: 50, unit: 'px'},
                },
              },
            })}
            layout={Layout.Sleek}
            {...settings}
          />
        </header>
      </div>
      <div className={s.main}>
        <section className={s.section}>
          <Cell className={s.title}>
            <ProductName name={product.name} />
          </Cell>
          {renderProductPageSlots && (
            <Cell className={classNames({[s.productPageSlot]: isPluginInstalledInDetails1Slot})}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails1} />
            </Cell>
          )}
          <RatingSummarySlot />
          <ConditionalRender by="productPage_productPrice">
            <Cell className={s.price}>
              <ProductPrice />
            </Cell>
          </ConditionalRender>
          {renderProductPageSlots && (
            <Cell
              className={classNames({
                [s.productPageSlot]: isPluginInstalledInDetails3Slot,
                [s.productPageSlotBottomSpacingSmall]: isPluginInstalledInDetails3Slot,
              })}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails3} />
            </Cell>
          )}

          {hasDiscountName && (
            <ConditionalRender by="productPage_showDiscountName">
              <Cell>
                <ProductDiscountName name={product.itemDiscount.discountRuleName} />
              </Cell>
            </ConditionalRender>
          )}

          {hasPaymentOptionsBreakdown && (
            <ConditionalRender by="productPage_paymentOptionsBreakdown">
              <Cell>
                <ProductPaymentBreakdown />
              </Cell>
            </ConditionalRender>
          )}

          <Cell>
            <DetailsPlaceholder />
          </Cell>
          <div>
            <Cell className={s.description}>
              <ProductDescription description={product.description} />
            </Cell>
          </div>
          {renderProductPageSlots && (
            <Cell className={classNames({[s.productPageSlotMediumSpacing]: isPluginInstalledInDetails4Slot})}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails4} />
            </Cell>
          )}
          <div>
            <Cell className={s.options}>
              <ProductOptions />
              <ProductPageButtonsContainer />
            </Cell>
          </div>
          <ConditionalRender by="productPage_productSku">
            <Cell className={s.sku}>
              <ProductSku />
            </Cell>
          </ConditionalRender>
          {renderProductPageSlots && (
            <Cell
              className={classNames(s.productPageSlot, {
                [s.productPageSlotBottomSpacingMedium]: isPluginInstalledInDetails2Slot,
              })}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails2} />
            </Cell>
          )}
          <ConditionalRender by="productPage_socialNetworks">
            <Cell className={s.social}>
              <ProductSocial />
            </Cell>
          </ConditionalRender>
          {renderProductPageSlots && (
            <Cell className={classNames({[s.productPageSlotBigSpacing]: isPluginInstalledInDetails8Slot})}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails8} />
            </Cell>
          )}
        </section>
      </div>
      <ConditionalRender by="productPage_productInfoSection">
        <div className={s.footerContainer}>
          <footer className={s.footer}>
            <Cell className={s.info}>
              <InfoSectionLayout />
            </Cell>
          </footer>
        </div>
      </ConditionalRender>
      {renderProductPageSlots && (
        <Cell className={classNames({[s.productPageSlotBottomSpacingBig]: isPluginInstalledInDetails9Slot})}>
          <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails9} />
        </Cell>
      )}
      {renderProductPageSlots && (
        <Cell className={classNames({[s.productPageSlot]: isPluginInstalledInBottomSlot})}>
          <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageBottom} />
        </Cell>
      )}
      <Cell>
        <ReviewsSlot />
        <BottomPlaceholder />
      </Cell>
    </article>
  );
};
